import { AnimatePresence } from 'framer-motion';
import { FC, MouseEvent, useState } from 'react';
import styled, { css } from 'styled-components';

import Button from '@/components/Button';
import Checkbox from '@/components/Checkbox';
import FancySwitch from '@/components/FancySwitch';
import { Input, Textarea } from '@/components/Forms';
import ShowHideButton from '@/components/ShowHideButton';
import Text from '@/components/Text';

import AttachedCountBadge from '../AttachedCountBadge';
import MoreImagesModal from '../MoreImagesModal';
import UploadMultipleImages from '../UploadMultipleImages';
import {
  AddMoreButton,
  Box,
  ButtonHolder,
  Header,
  HeaderButtons,
  Heading,
  ImageData,
  LabelText,
  MoreImages,
  ResetButton,
  UploadedImages,
} from '../VariationOne';
import { Filler, getWatermarkPosition, getWatermarkTag } from '../shared';
import { ActionBox, ActionTitle } from '../shared/styledComponents';
import GeneratedImages from './GeneratedImages';
import UploadedImageRow from './UploadedImageRow';

export const DIVIDERS = ['none', 'black'];

export interface ImageVariation {
  leftImageSrc: string;
  rightText: string;
  leftWatermarkTag: string;
  leftWatermarkPosition: string;
  divider: string;
  quotes: boolean;
  brakes: boolean;
  fontSize?: number;
  reversed?: boolean;
}

export const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
`;

export const StyledUploadedImages = styled(UploadedImages)`
  height: 382px;
`;

export const UploadedMultipleImages = styled(UploadedImages)`
  max-height: 540px;
`;

export const TextAreaBox = styled.div<{ $reversed?: boolean }>`
  padding: 24px;
  border-left: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 16px;
  flex-grow: 1;
  align-content: start;
  ${({ $reversed }) =>
    $reversed &&
    css`
      border-left: 0;
    `}
`;

export const StyledTextArea = styled(Textarea)`
  height: 168px;
`;

export const StyledUploadMultipleImages = styled(UploadMultipleImages)`
  height: 358px;
`;

export const TextPlaceholder = styled.div`
  height: 73px;
  border: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
  border-top: 2px solid ${({ theme }) => theme.colors.grayscaleLight};
  border-right: 0;
`;

export const ImagesBox = styled.div<{
  $hasImages?: boolean;
  $hasLeftBorder?: boolean;
  $reversed?: boolean;
}>`
  padding: 24px;
  ${({ $hasImages }) =>
    $hasImages &&
    css`
      padding-bottom: 0;
      border-bottom: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
    `}
  ${({ $hasLeftBorder }) =>
    $hasLeftBorder &&
    css`
      border-left: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
    `}
`;

export const ActionsHolder = styled.div`
  padding: 24px;
  border-top: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
  &:nth-child(even) {
    border-left: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
  }
`;

export const ActionsHolderFull = styled(ActionsHolder)`
  grid-column: span 2;
`;

export const ActionsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
`;

export const TextParametersBox = styled.div<{ $reversed?: boolean }>`
  ${({ $reversed }) =>
    $reversed &&
    css`
      order: -1;
    `}
`;

interface Props {
  inArticle?: boolean;
}

const VariationTwo: FC<Props> = ({ inArticle }) => {
  const [showContent, setShowContent] = useState(true);
  const [text, setText] = useState('');
  const [multipleVariations, setMultipleVariations] = useState(false);
  const [isReversed, setIsReversed] = useState(false);

  const [quotes, setQuotes] = useState(true);
  const [brakes, setBrakes] = useState(false);
  const [leftImages, setLeftImages] = useState<ImageData[]>([]);
  const [fontSize, setFontSize] = useState('');
  const [showMoreImagesModal, setShowMoreImagesModal] = useState(false);
  const [variations, setVariations] = useState<ImageVariation[]>([]);
  const [images, setImages] = useState<string[]>();

  const onReset = () => {
    setLeftImages([]);
    setVariations([]);
    setImages([]);
    setText('');
    setBrakes(false);
    setQuotes(true);
    setFontSize('');
  };

  const generateVariations = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    const newVariations = leftImages.map((leftImage) => ({
      leftImageSrc:
        leftImage.croppedImage ?? URL.createObjectURL(leftImage.image),
      leftWatermarkTag: getWatermarkTag(leftImage),
      leftWatermarkPosition: getWatermarkPosition(leftImage),
      rightText: multipleVariations ? leftImage.text : text,
      quotes: !multipleVariations ? quotes : leftImage.quotes,
      brakes: !multipleVariations ? brakes : leftImage.brakes,
      divider: leftImage.divider,
      fontSize: !multipleVariations
        ? parseInt(fontSize)
        : leftImage.fontSize ?? 0,
      reversed: isReversed,
    }));

    setVariations(newVariations);
  };

  const renderLeftImages = leftImages.map((image, index) => (
    <UploadedImageRow
      key={`${index}-${image.image.name}-${image.image.lastModified}`}
      imageIndex={index}
      image={image}
      setImages={setLeftImages}
    />
  ));

  const renderMultipleLeftImages = leftImages.map((image, index) => (
    <UploadedImageRow
      key={`${index}-${image.image.name}-${image.image.lastModified}`}
      imageIndex={index}
      image={image}
      setImages={setLeftImages}
      hasText
      multipleVariations
    />
  ));

  const isResetDisabled =
    !leftImages?.length && !text && quotes && !brakes && !fontSize;

  return (
    <>
      <Box>
        <Header>
          <div>
            <Heading $type="button">Image + Text</Heading>
            <Text $type="smallHeadline" $color="gray">
              Attach one image on the left side and add text on the right with
              the necessary parameters
            </Text>
          </div>
          <HeaderButtons>
            <FancySwitch
              isActive={isReversed}
              onClick={() => setIsReversed((prev) => !prev)}
              text="Reversed"
            />
            <FancySwitch
              isActive={multipleVariations}
              onClick={() => setMultipleVariations((prev) => !prev)}
              text="Multiple Variations:"
            />
            <ResetButton
              color="red"
              disabled={isResetDisabled}
              onClick={onReset}
            >
              Reset
            </ResetButton>
            <ShowHideButton
              showContent={showContent}
              onClick={() => setShowContent((prev) => !prev)}
            />
          </HeaderButtons>
        </Header>
        {showContent && (
          <>
            {multipleVariations ? (
              <ImagesBox $hasImages={!!leftImages?.length}>
                <LabelText $type="smallHeadline">
                  {isReversed ? 'Right' : 'Left'} Images
                </LabelText>
                {!!leftImages?.length ? (
                  <>
                    <UploadedMultipleImages>
                      {renderMultipleLeftImages}
                    </UploadedMultipleImages>
                    <MoreImages>
                      <AttachedCountBadge count={leftImages.length} />
                      <AddMoreButton
                        onClick={() => setShowMoreImagesModal(true)}
                        color="colorized"
                      >
                        Add more images
                      </AddMoreButton>
                    </MoreImages>
                  </>
                ) : (
                  <UploadMultipleImages
                    coverIndex={1}
                    noneDivider
                    setImages={setLeftImages}
                  />
                )}
              </ImagesBox>
            ) : (
              <ImageGrid>
                <ImagesBox
                  $hasLeftBorder={isReversed}
                  $hasImages={!!leftImages?.length}
                >
                  <LabelText $type="smallHeadline">
                    {isReversed ? 'Right' : 'Left'} Images
                  </LabelText>
                  {!!leftImages?.length ? (
                    <>
                      <StyledUploadedImages>
                        {renderLeftImages}
                      </StyledUploadedImages>
                      <MoreImages>
                        <AttachedCountBadge count={leftImages.length} />
                        <AddMoreButton
                          onClick={() => setShowMoreImagesModal(true)}
                          color="colorized"
                        >
                          Add more images
                        </AddMoreButton>
                      </MoreImages>
                    </>
                  ) : (
                    <StyledUploadMultipleImages
                      coverIndex={2}
                      noneDivider
                      setImages={setLeftImages}
                    />
                  )}
                </ImagesBox>
                <TextParametersBox $reversed={isReversed}>
                  <TextAreaBox $reversed={isReversed}>
                    <div>
                      <LabelText $type="smallHeadline">
                        {isReversed ? 'Left' : 'Right'} Text
                      </LabelText>
                      <StyledTextArea
                        onChange={(e) => setText(e.target.value)}
                        value={text}
                        placeholder="Add text"
                      ></StyledTextArea>
                    </div>
                    <div>
                      <LabelText $type="smallHeadline">Font Size</LabelText>
                      <Input
                        value={fontSize}
                        onChange={(e) => setFontSize(e.target.value)}
                        placeholder="Font Size (px)"
                        type="number"
                        min="0"
                      />
                    </div>
                    <div>
                      <ActionTitle $type="smallHeadline">
                        Text Parameters
                      </ActionTitle>
                      <ActionBox>
                        <Checkbox
                          checked={quotes}
                          onChange={() => setQuotes((prev) => !prev)}
                        >
                          Quotes
                        </Checkbox>
                        <Checkbox
                          checked={brakes}
                          onChange={() => setBrakes((prev) => !prev)}
                        >
                          Custom brakes
                        </Checkbox>
                      </ActionBox>
                    </div>
                  </TextAreaBox>
                  {!!leftImages?.length && <TextPlaceholder />}
                </TextParametersBox>
              </ImageGrid>
            )}
          </>
        )}
      </Box>
      <Filler fontFamily="Gothic" />
      {showContent && (
        <ButtonHolder>
          <Button disabled={!leftImages?.length} onClick={generateVariations}>
            {!!variations?.length ? 'Regenerate' : 'Generate'}
          </Button>
        </ButtonHolder>
      )}
      <GeneratedImages
        variations={variations}
        images={images}
        setImages={setImages}
        inArticle={inArticle}
        onReset={onReset}
      />
      <AnimatePresence>
        {!!showMoreImagesModal && (
          <MoreImagesModal
            onClose={() => setShowMoreImagesModal(false)}
            setImages={setLeftImages}
            coverIndex={1}
            noneDivider
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default VariationTwo;
